import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const StarsDisplay = ({ value = 0, size = "h-4 w-4" }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map((n) => (
      <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-marigold text-marigold" : "text-deepbrown/20"}`} />
    ))}
  </div>
);

export default function ProductReviews({ productId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = () => api.get(`/products/${productId}/reviews`).then((r) => setData(r.data));
  useEffect(() => { refresh(); }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Please sign in to write a review");
    if (!comment.trim()) return toast.error("Please write your review");
    setSubmitting(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, title, comment });
      toast.success("Review posted");
      setTitle(""); setComment(""); setRating(5);
      refresh();
    } catch (e) {
      toast.error("Could not post review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-16" data-testid="reviews-section">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl sm:text-3xl text-deepbrown font-bold">Customer Reviews</h2>
        {data.count > 0 && (
          <div className="flex items-center gap-2">
            <StarsDisplay value={data.average} size="h-5 w-5" />
            <span className="font-bold text-deepbrown">{data.average}</span>
            <span className="text-sm text-deepbrown/60">({data.count} reviews)</span>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {/* Review form */}
        <form onSubmit={submit} className="bg-white rounded-2xl p-5 border border-deepbrown/10 h-fit" data-testid="review-form">
          <h3 className="font-display text-lg text-deepbrown">Write a review</h3>
          <div className="mt-3">
            <p className="text-xs uppercase tracking-widest font-bold text-deepbrown/70 mb-1">Your rating</p>
            <div className="flex gap-1">
              {[1,2,3,4,5].map((n) => (
                <button type="button" key={n} onClick={() => setRating(n)} data-testid={`rating-${n}`}>
                  <Star className={`h-7 w-7 ${n <= rating ? "fill-marigold text-marigold" : "text-deepbrown/20"}`} />
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3">
            <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="review-title" />
          </div>
          <div className="mt-3">
            <Textarea placeholder="Share your experience…" value={comment} onChange={(e) => setComment(e.target.value)} rows={4} data-testid="review-comment" />
          </div>
          <button type="submit" disabled={submitting} className="mt-3 w-full py-2.5 rounded-full bg-rani text-white font-bold disabled:opacity-50" data-testid="review-submit">
            {submitting ? "Posting…" : "Post Review"}
          </button>
        </form>

        {/* List */}
        <div className="space-y-3">
          {data.reviews.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 border border-deepbrown/10 text-center text-deepbrown/60">
              Be the first to review this product.
            </div>
          ) : data.reviews.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl p-5 border border-deepbrown/10" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-display text-deepbrown">{r.user_name}</p>
                <StarsDisplay value={r.rating} />
              </div>
              {r.title && <p className="font-bold text-deepbrown mt-1">{r.title}</p>}
              <p className="text-sm text-deepbrown/80 mt-1 leading-relaxed">{r.comment}</p>
              <p className="text-[10px] uppercase tracking-widest text-deepbrown/40 mt-3">
                {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
